import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Paper, Table as TableMUI, TableBody, TableRow, TableCell, TableContainer, TableHead, TextField } from "@material-ui/core";

import { ClickAwayListener, Fade, MenuItem, MenuList, Popper } from "@mui/material";
import MoreVertOutlined from "@mui/icons-material/MoreVertOutlined";
import { omniSearch } from "../../../helpers/searchUtility";

import "./Table.css";
import TableInput from "../tableInput/TableInput";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const SORT_DIRECTION = {
  ASCENDING: "ASCENDING",
  DECENDING: "DECENDING",
};

export default function Table({
  headers = [],
  rows = [],
  onRowClick,
  onRowHover,
  tableHeight = "80vh",
  rowActions = [],
  getRowActions = null,
  searchable = false,
  datepicker = false,
  emptyMessage = null,
}) {
  const [_rows, _setRows] = useState([...rows]);
  const [_searchPhrase, _setSearchPhrase] = useState("");
  const [_date, _setDate] = useState(new Date());
  const [_lastSort, _setlastSort] = useState({
    key: "",
    sort: SORT_DIRECTION.ASCENDING,
  });

  // Row actions menu — which row id is open, and Popper anchor (virtual = click coords)
  const [_openRow, _setOpenRow] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const closeMenu = React.useCallback((e) => {
    _setOpenRow(null);
    setAnchorEl(null);
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
  }, []);

  const handleContextMenu = (e, row) => {
    e.stopPropagation();
    const { clientX, clientY } = e;
    _setOpenRow(row.id);
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: clientY,
        right: clientX,
        bottom: clientY,
        left: clientX,
      }),
    };
    setAnchorEl(virtualElement);
  };

  const handleRowAction = (event, func, targetedRow) => {
    closeMenu(event);
    if (targetedRow != null) func(targetedRow);
  };

  useEffect(() => {
    const onScrollCapture = () => {
      closeMenu();
    };
    window.addEventListener("scroll", onScrollCapture, true);

    return () => {
      window.removeEventListener("scroll", onScrollCapture, true);
    };
  }, [closeMenu]);

  // Table functions
  const handleSort = (key) => {
    // Sort Ascendingly if user is sorting by a new key or if the user has already sorted by this key descendingly
    if (key !== _lastSort.key || (key === _lastSort.key && _lastSort.sort === SORT_DIRECTION.DECENDING)) {
      _setRows(_sort(key, _rows, SORT_DIRECTION.ASCENDING));
      _setlastSort({ key: key, sort: SORT_DIRECTION.ASCENDING });
      // In any other condition, sort descendingly
    } else {
      _setRows(_sort(key, _rows, SORT_DIRECTION.DECENDING));
      _setlastSort({ key: key, sort: SORT_DIRECTION.DECENDING });
    }
  };

  const _sort = useCallback((key, rowsToSort, sortDirection) => {
    const compare = (a, b) => {
      const va = a[key];
      const vb = b[key];
      const na = Number(va);
      const nb = Number(vb);
      const aIsNum =
        va != null && va !== "" && Number.isFinite(na) && String(va).trim() !== "";
      const bIsNum =
        vb != null && vb !== "" && Number.isFinite(nb) && String(vb).trim() !== "";
      if (aIsNum && bIsNum) return na - nb;
      const sa = va == null ? "" : String(va);
      const sb = vb == null ? "" : String(vb);
      if (sa < sb) return -1;
      if (sa > sb) return 1;
      return 0;
    };
    const sorted = [...rowsToSort].sort(compare);
    if (sortDirection === SORT_DIRECTION.ASCENDING) return sorted;
    return sorted.reverse();
  }, []);

  // Prevents recomputing of rows unless the rows or the search phrase change
  const handleTableSearch = useCallback(() => {
    let searchPhrase = _searchPhrase.trim();
    _setRows(omniSearch(rows, searchPhrase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_searchPhrase, rows]);

  // Search whenever the search phrase is changed
  useEffect(() => {
    handleTableSearch();
  }, [_searchPhrase, handleTableSearch]);

  const showActionsColumn =
    typeof getRowActions === "function" ||
    (Array.isArray(rowActions) && rowActions.length > 0);

  return (
    <>
      <div className="table-controls-container">
        {searchable && (
          <div className="table-search-bar-container">
            <TableInput
              type="text"
              inputClassName="table-search-bar"
              InputProps={{ disableUnderline: true }}
              value={_searchPhrase}
              onChange={(e) => _setSearchPhrase(e.target.value)}
              placeholder="Search ...."
            />
          </div>
        )}
        {datepicker && (
          <div className="date-picker-container">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                label="Date"
                inputFormat="dd/MM/yyyy"
                value={_date}
                disableFuture={true}
                onChange={(value) => _setDate(value)}
                text={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </div>
        )}
      </div>
      <Paper>
        <TableContainer style={{ height: tableHeight }}>
          <TableMUI stickyHeader aria-label="sticky table" onScroll={closeMenu}>
            <TableHead>
              <TableRow className="head">
                {headers.map((header) => (
                  <TableCell
                    key={header.key}
                    className="header-cell"
                    onClick={() => {
                      handleSort(header.key);
                    }}
                  >
                    <div className="header-cell-content">
                      {header.name}
                      {/* <SortIcon /> */}
                    </div>
                  </TableCell>
                ))}
                {showActionsColumn && (
                    <TableCell key="menu" className="header-cell">
                      <div className="header-cell-content" />
                    </TableCell>
                  )}
              </TableRow>
            </TableHead>
            <TableBody>
              {_rows.length === 0 && emptyMessage && (
                <TableRow className="row table-empty-row">
                  <TableCell colSpan={headers.length + (showActionsColumn ? 1 : 0)} align="center" className="table-empty-message-cell">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
              {_rows.map((row, rowIndex) => {
                const actionsForRow =
                  typeof getRowActions === "function"
                    ? getRowActions(row) || []
                    : rowActions;

                return (
                <TableRow
                  data-tip={onRowHover && onRowHover}
                  key={row.id != null && row.id !== "" ? String(row.id) : `row-${rowIndex}`}
                  onClick={onRowClick ? () => onRowClick(row) : () => {}}
                  className="row"
                  style={onRowClick && { cursor: "pointer" }}
                >
                  {headers.map((header) => (
                    <TableCell key={header.key} align="center">
                      {header.formatter ? header.formatter(row) : (row[header.key] ?? "N/A")}
                      {""}
                      {header.suffix && header.suffix}
                    </TableCell>
                  ))}
                  {actionsForRow.length > 0 && (
                      <TableCell key="menu">
                        <MoreVertOutlined onClick={(e) => handleContextMenu(e, row)} />
                        <Popper
                          id={row.id}
                          open={anchorEl != null && _openRow != null && String(_openRow) === String(row.id)}
                          anchorEl={anchorEl}
                          transition
                          placement="bottom-start"
                          style={{ zIndex: 1300 }}
                        >
                          {({ TransitionProps }) => (
                            <ClickAwayListener onClickAway={(ev) => closeMenu(ev)} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                              <div>
                                <Fade {...TransitionProps}>
                                  <MenuList dense>
                                    {actionsForRow.map((action) => (
                                      <MenuItem
                                        key={`${row.id}-${action.name}`}
                                        onClick={(e) => {
                                          const targetedRow = rows.find((r) => String(r.id) === String(_openRow));
                                          handleRowAction(e, action.func, targetedRow);
                                        }}
                                      >
                                        {action.icon}
                                        {action.name}
                                      </MenuItem>
                                    ))}
                                  </MenuList>
                                </Fade>
                              </div>
                            </ClickAwayListener>
                          )}
                        </Popper>
                      </TableCell>
                    )}
                </TableRow>
              );
              })}
            </TableBody>
          </TableMUI>
        </TableContainer>
      </Paper>
    </>
  );
}

Table.propTypes = {
  headers: PropTypes.array,
  rows: PropTypes.array,
  onRowClick: PropTypes.func,
  onRowHover: PropTypes.func,
  searchable: PropTypes.bool,
  emptyMessage: PropTypes.string,
  getRowActions: PropTypes.func,
};
